import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { flushSync } from "react-dom";
import { useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ReCAPTCHA from "react-google-recaptcha";
import { Banner } from "@smartling-ui/banner";
import { ExternalLink, Link } from "@smartling-ui/link";

import RegistrationForm from "../RegistrationForm";
import GoogleButton from "../GoogleButton";
import { GoogleRegistrationForm } from "../GoogleRegistrationForm";
import { FormStepLayout } from "../FormStepLayout";
import { SignInLink } from "../SignInLink";

import {
    RegisterAccountServerErrorResponse,
    RegistrationErrorMessage,
    RegistrationFormType
} from "../../types";
import {
    parseRegisterAccountErrors,
    redirectToAccountPage,
    redirectToTrialConfiguration
} from "../../utils";
import { registerAccount } from "../../api";
import validationMessages from "../../validationMessages";
import { CONTACT_SALES_URL } from "../../constants";

import { getErrorMessageForGoogle } from "./utils";
import { getRegistrationSchema } from "./utils/getRegistrationSchema";
import { RECAPTCHA_SITE_KEY } from "./constants";
import messages from "./messages";
import * as styles from "./styles.module.css";

type Props = {
    productUid: string;
    provider: string;
    errorCode: string;
    hsCtaAttrib: string;
    hasDeveloperSandbox: boolean;
    onViewChange?: () => void;
}

declare global {
    interface Window {
        recaptchaOptions: {
            useRecaptchaNet: boolean;
        };
    }
}

const hasDataInsideError = (error: unknown): error is RegisterAccountServerErrorResponse => (
    typeof error === "object" && error !== null && "data" in error
);

// Shared form-level fallback shown whenever there is no more specific message:
// success without an accountUid, an empty/unparseable error response, or an
// unexpected/network failure.
const genericFormError: RegistrationErrorMessage = { descriptor: validationMessages.genericError };

// Stable anchor for the signupBlocked banner's <a>…</a> chunk. Defined at module
// scope (not inline) to avoid a per-render allocation and the react/jsx-no-bind
// lint; react-intl ignores it for messages that contain no <a> tag.
function ContactSalesAnchor(chunks: React.ReactNode[]) {
    return (
        <ExternalLink
            href={CONTACT_SALES_URL}
            size="inherit"
            weight="inherit"
            color="inherit"
        >
            {chunks}
        </ExternalLink>
    );
}

export function RegistrationFormController({
    productUid,
    provider,
    errorCode,
    hsCtaAttrib,
    hasDeveloperSandbox,
    onViewChange
}: Props) {
    const intl = useIntl();
    const reCaptchaRef = useRef<ReCAPTCHA>(null);
    const [activeView, setActiveView] = useState<"email" | "google">(
        provider === "google" ? "google" : "email"
    );
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState<RegistrationErrorMessage | null>(null);
    const [googleErrorCode, setGoogleErrorCode] = useState(errorCode);

    useEffect(() => {
        window.recaptchaOptions = { useRecaptchaNet: true };
    }, []);

    const schema = useMemo(
        () => getRegistrationSchema(intl, hasDeveloperSandbox),
        [intl, hasDeveloperSandbox]
    );

    const {
        control,
        handleSubmit,
        setError,
        clearErrors,
        getValues
    } = useForm<RegistrationFormType>({
        resolver: zodResolver(schema),
        mode: "onTouched",
        defaultValues: {
            accountName: "",
            companyName: "",
            jobTitle: "",
            email: "",
            password: "",
            confirmPassword: "",
            acceptPrivacyPolicy: false
        }
    });

    const errorMessageForGoogle = useMemo(
        () => getErrorMessageForGoogle(provider, googleErrorCode),
        [googleErrorCode, provider]
    );

    // Submit-driven form error takes precedence over the URL-derived Google errorCode.
    const formLevelError = formError ?? errorMessageForGoogle;

    const banner = formLevelError ? (
        <Banner variant="error">
            {intl.formatMessage(formLevelError.descriptor, {
                ...formLevelError.values,
                a: ContactSalesAnchor
            })}
        </Banner>
    ) : null;

    const handleSwitchToGoogle = useCallback(() => {
        setFormError(null);
        const switchView = () => {
            flushSync(() => setActiveView("google"));
            onViewChange?.();
        };

        if (!document.startViewTransition) {
            switchView();
        } else {
            document.startViewTransition(switchView);
        }

        const url = new URL(window.location.href);
        url.searchParams.set("provider", "google");
        window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    }, [onViewChange]);

    const handleSwitchToEmail = useCallback(() => {
        setFormError(null);
        setGoogleErrorCode("");
        const switchView = () => {
            flushSync(() => setActiveView("email"));
            onViewChange?.();
        };

        if (!document.startViewTransition) {
            switchView();
        } else {
            document.startViewTransition(switchView);
        }

        const url = new URL(window.location.href);
        url.searchParams.delete("provider");
        url.searchParams.delete("errorCode");
        window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    }, [onViewChange]);

    const preventBlur = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
    }, []);

    const onSubmit = useCallback(async (form: RegistrationFormType) => {
        try {
            setLoading(true);
            setFormError(null);
            clearErrors();

            /* istanbul ignore next */
            if (!reCaptchaRef.current) {
                setLoading(false);
                return;
            }

            const token = await reCaptchaRef.current.executeAsync();
            reCaptchaRef.current.reset();

            /* istanbul ignore next: difficult to imitate the value of a token */
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                const result = await registerAccount(
                    productUid,
                    form,
                    token,
                    hasDeveloperSandbox,
                    hsCtaAttrib
                );

                const { accountUid } = result.response.data;

                if (!accountUid) {
                    console.error("Registration succeeded without accountUid", result);
                    setFormError(genericFormError);
                    setLoading(false);
                    return;
                }

                if (hasDeveloperSandbox) {
                    redirectToTrialConfiguration(accountUid);
                } else {
                    redirectToAccountPage(accountUid);
                }
            } catch (error: unknown) {
                console.error("Registration error", error);

                if (hasDataInsideError(error)) {
                    const { fieldErrors, formError: parsedFormError } = parseRegisterAccountErrors(
                        error,
                        hasDeveloperSandbox
                    );

                    Object.entries(fieldErrors).forEach(([field, message]) => {
                        if (message) {
                            setError(
                                field as keyof RegistrationFormType,
                                {
                                    type: message.richType ?? "server",
                                    message: intl.formatMessage(message.descriptor, message.values)
                                }
                            );
                        }
                    });

                    if (parsedFormError) {
                        setFormError(parsedFormError);
                    } else if (Object.keys(fieldErrors).length === 0) {
                        setFormError(genericFormError);
                    }
                } else {
                    setFormError(genericFormError);
                }

                setLoading(false);
            }
        } catch (error) {
            console.error("Registration error", error);
            setFormError(genericFormError);
            setLoading(false);
        }
    }, [
        productUid,
        setError,
        clearErrors,
        intl,
        hasDeveloperSandbox,
        hsCtaAttrib
    ]);

    const googleHeading = intl.formatMessage(
        hasDeveloperSandbox ? messages.headerSetUpAccount : messages.headerReviewTerms
    );

    return activeView === "email" ? (
        <FormStepLayout
            heading={intl.formatMessage(messages.createYourAccount)}
            description={intl.formatMessage(messages.signInDescription)}
            footer={<SignInLink />}
        >
            <form
                onSubmit={handleSubmit(onSubmit)}
                className={styles.form}
            >
                <div>
                    <ReCAPTCHA
                        sitekey={RECAPTCHA_SITE_KEY}
                        size="invisible"
                        ref={reCaptchaRef}
                    />

                    <GoogleButton onClick={handleSwitchToGoogle} />
                </div>

                {banner}

                <div className={styles.or}>
                    {intl.formatMessage(messages.or)}
                </div>

                <RegistrationForm
                    loading={loading}
                    control={control}
                    hasDeveloperSandbox={hasDeveloperSandbox}
                />
            </form>
        </FormStepLayout>
    ) : (
        <FormStepLayout
            heading={googleHeading}
            description={intl.formatMessage(
                hasDeveloperSandbox
                    ? messages.googleFormDescription
                    : messages.googleFormDescriptionTermsOnly
            )}
            footer={(
                <div className={styles.googleFooter}>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <Link asChild size="sm">
                        <button
                            data-testid="googleFormBackLink"
                            type="button"
                            onMouseDown={preventBlur}
                            onClick={handleSwitchToEmail}
                        >
                            {intl.formatMessage(messages.backToSignUp)}
                        </button>
                    </Link>
                    <span className={styles.googleFooterDivider} />
                    <Link href="/" size="sm">
                        {intl.formatMessage(messages.logIn)}
                    </Link>
                </div>
            )}
        >
            {banner}

            <GoogleRegistrationForm
                productUid={productUid}
                hasDeveloperSandbox={hasDeveloperSandbox}
                initialCompanyName={getValues("companyName")}
                initialJobTitle={getValues("jobTitle")}
                hsCtaAttrib={hsCtaAttrib}
            />
        </FormStepLayout>
    );
}
